/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

// Helper Imports
import { formatFullName, formatPhone } from "../../../../Helpers/formatters";
import { validateUserDetails } from "../../../../Helpers/validateInputData";

// MUI Imports
import Checkbox from "@mui/material/Checkbox";
import EditIcon from "@mui/icons-material/Edit";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import WarningSharpIcon from "@mui/icons-material/WarningSharp";
import CheckIcon from "@mui/icons-material/Check";

// Component Imports
import EditModal from "../../../../Components/EditModal";
import Loading from "../../../../Components/Interactive/Loading";
import ToolTip from "../../../../Components/Interactive/ToolTip";

const CustomerDetailsWidget = ({
  allCustomerData,
  loading,
  setCustomerData,
  setUpdatedData,
  setTrigger,
}) => {
  const {
    userDetailsInfo: {
      firstname,
      lastname,
      email,
      phone,
      customerId,
      isCustomerActive,
      postalCode,
      emailVerification,
      hasUnlimitedPdf,
    },
    userReportDownloads: { reportDownloads },
  } = allCustomerData;
  const userAuth = useSelector((state) => state.auth);
  const isAdminOrSuperUser =
    userAuth?.userInfo?.userData?.role === "Administrator" ||
    userAuth?.userInfo?.userData?.role === "Super User" ||
    userAuth?.userInfo?.userData?.role === "Supervisor";
  const userId = userAuth?.userInfo?.userData?.userId;
  const [customerFirstName, setFirstName] = useState("");
  const [customerLastName, setLastName] = useState("");
  const [customerEmail, setEmail] = useState("");
  const [customerPhone, setPhone] = useState("");
  const [initialState, setInitialState] = useState({
    firstname,
    lastname,
    email,
    phone,
  });
  const [customerHasUnlimitedPdf, setCustomerHasUnlimitedPdf] = useState(false);
  const [checked, setChecked] = useState(true);
  const [modalIsOpen, setModalOpen] = useState(false);

  // Get customers current data
  useEffect(() => {
    setFirstName(firstname);
    setLastName(lastname);
    setEmail(email);
    setPhone(phone);
    setInitialState({ firstname, lastname, email, phone });
    setCustomerHasUnlimitedPdf(hasUnlimitedPdf);
  }, [firstname, lastname, email, phone, hasUnlimitedPdf]);

  // Event Handlers
  const handleChange = (e) => setChecked(e.target.checked);
  const handleOpen = (e) => setModalOpen(true);
  const handleClose = (e) => {
    const { firstname, lastname, email, phone } = initialState;
    setFirstName(firstname);
    setLastName(lastname);
    setEmail(email);
    setPhone(phone);
    setModalOpen(false);
  };

  const flagBadActor = async () => {
    const res = await fetch(
      `/api/customer/${customerId}/flag-bad-actor?userId=${userId}&reportDownloadId=${reportDownloads.id}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Details: null,
        }),
      }
    );

    const { success } = await res.json();

    setTrigger("UPDATE_REPORT_DOWNLOADS");

    setUpdatedData((prevState) => ({
      updateData: {
        state: success ? true : false,
        message: success
          ? "Successfully flagged bad actor"
          : "Failed to flag bad actor",
      },
    }));
  };

  const submitUserDetailsUpdate = async () => {
    const error = validateUserDetails(
      customerFirstName,
      customerLastName,
      customerEmail,
      customerPhone
    );
    if (error) return;

    // Send API request to update user data and update the top level allCustomerData state
    const detailsOptions = {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firstname: customerFirstName,
        lastname: customerLastName,
        email: customerEmail,
        phone: customerPhone,
        hasUnlimitedPdf: customerHasUnlimitedPdf,
        userId: userAuth?.userInfo?.userData?.userId,
      }),
    };
    const resForDetails = await fetch(
      `/api/customer/${customerId}/update-customer`,
      detailsOptions
    );

    const isSuccess = resForDetails.json();

    let memberUpdateMessage;

    if (checked) {
      const membershipOptions = {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          MembershipId:
            allCustomerData?.userMembershipInfo?.membershipInfo[0]
              ?.membershipId,
          // Dont update the username if it's the same as the current one
          Username:
            customerEmail ===
            allCustomerData?.userMembershipInfo?.membershipInfo[0]
              ?.memberUsername
              ? null
              : customerEmail,
          ExpirationDate:
            allCustomerData?.userMembershipInfo?.membershipInfo[0]
              ?.expirationDate,
        }),
      };
      const updateMemberRes = await fetch(
        `/api/customer/${customerId}/update-membership`,
        membershipOptions
      );
      const { hasExistingMembershipUsername } = await updateMemberRes.json();

      // Display an error message if the membership email is already taken on another customer
      if (hasExistingMembershipUsername) {
        memberUpdateMessage =
          "Could not update membership: email already in use on an existing membership";
      }
    }
    setCustomerData((prevState) => ({
      userDetailsInfo: {
        ...prevState.userDetailsInfo,
        firstname: customerFirstName,
        lastname: customerLastName,
        email: customerEmail,
        phone: customerPhone,
      },
      userOrders: prevState.userOrders,
      userPaymentInfo: prevState.userPaymentInfo,
      dispositions: prevState.dispositions,
      userCallNotesInfo: prevState.userCallNotesInfo,
      userEmailNotesInfo: prevState.userEmailNotesInfo,
      userMembershipInfo: {
        ...prevState.userMembershipInfo,
        membershipInfo: prevState.userMembershipInfo.membershipInfo.map(
          (membership) => {
            if (checked && !memberUpdateMessage)
              membership["memberUsername"] = customerEmail;
            return membership;
          }
        ),
      },
      userTrackingInfo: prevState.userTrackingInfo,
      userProgramsInfo: prevState.userProgramsInfo,
      userLoginHistoryInfo: prevState.userLoginHistoryInfo,
      userEvents: prevState.userEvents,
      userReportHistory: prevState.userReportHistory,
      userCallLog: prevState.userCallLog,
      userLockouts: prevState.userLockouts,
      userReportDownloads: prevState.userReportDownloads,
    }));

    let pageMessage;
    if (!isSuccess) pageMessage = "Failed to update users details";
    else if (memberUpdateMessage) pageMessage = memberUpdateMessage;
    else pageMessage = "User details updated";

    setUpdatedData((prevState) => ({
      updateData: {
        ...prevState.updateDataSuccess,
        state: isSuccess && !memberUpdateMessage ? true : false,
        message: pageMessage,
      },
    }));
    setModalOpen(false);
  };

  return (
    <div
      className="user-data-container customer-details"
      id="customer-details"
    >
      {loading ? (
        <div className="loading-widget">
          <Loading />
        </div>
      ) : (
        firstname &&
        lastname && (
          <h1 style={{ color: "#6acde6", fontSize: "1.5rem" }}>
            {formatFullName(firstname, lastname)}

            {!isCustomerActive && (
              <>
                <ToolTip
                  placement="left"
                  titleHeading="Inactive Customer"
                >
                  <span>
                    {" "}
                    <WarningSharpIcon
                      sx={{
                        color: "red !important",
                        size: "8px",
                        marginBottom: "-5px",
                      }}
                    />
                  </span>
                </ToolTip>
              </>
            )}
          </h1>
        )
      )}
      {phone && (
        <div className="user-detail">
          <h6>Phone</h6>
          <p>{formatPhone(phone)}</p>
        </div>
      )}
      {email && (
        <div className="user-detail">
          <h6>Email</h6>
          <p>{email}</p>
        </div>
      )}
      {emailVerification && (
        <>
          <ToolTip
            placement="left"
            titleHeading="Email Verified"
          >
            <span>
              {" "}
              <CheckIcon
                sx={{
                  color: "#99dda27e !important",
                  size: "8px",
                  marginBottom: "-5px",
                }}
              />
            </span>
          </ToolTip>
        </>
      )}
      {customerHasUnlimitedPdf && (
        <div className="user-detail">
          <h6>Unlimited Pdfs</h6>
        </div>
      )}
      {postalCode && (
        <div className="user-detail">
          <h6>Postal Code</h6>
          <p>{postalCode}</p>
        </div>
      )}
      {isAdminOrSuperUser && (
        <button
          className="submit-btn-details"
          onClick={flagBadActor}
        >
          <span>Flag Bad Actor</span>
        </button>
      )}

      <EditIcon
        className="edit-info-btn"
        onClick={handleOpen}
      />
      <EditModal
        open={modalIsOpen}
        handleClose={handleClose}
      >
        <div className="modal-header">
          <h2 style={{ padding: ".5rem", color: "#6acde6" }}>
            Edit Customer Details
          </h2>
          <CloseIcon
            className="close-icons"
            onClick={() => handleClose()}
          />
        </div>
        <TextField
          id="outlined-full-width"
          className="input-el"
          label="First Name"
          name="f_name"
          style={{ margin: 10, width: "96%" }}
          fullWidth={false}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          value={customerFirstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextField
          id="outlined-full-width"
          className="input-el"
          label="Last Name"
          name="l_name"
          style={{ margin: 8, width: "96%" }}
          fullWidth={false}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          value={customerLastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <TextField
          id="outlined-full-width"
          className="input-el"
          label="Email"
          name="email"
          style={{ margin: 8, width: "96%" }}
          fullWidth={false}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          value={customerEmail}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          id="outlined-full-width"
          className="input-el"
          label="Phone Number"
          name="phone"
          style={{ margin: 8, width: "96%" }}
          fullWidth={false}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          value={customerPhone}
          onChange={(e) => setPhone(e.target.value)}
        />

        <Checkbox
          checked={customerHasUnlimitedPdf}
          onChange={() => {
            setCustomerHasUnlimitedPdf(!customerHasUnlimitedPdf);
          }}
          color="secondary"
        />
        <small>Unlimited Pdfs</small>
        <Checkbox
          checked={checked}
          onChange={handleChange}
          color="secondary"
        />
        <small>Update membership email also</small>
        <div className="modal-btn-box">
          <button
            className="submit-btn-details"
            onClick={submitUserDetailsUpdate}
          >
            <span>Submit</span>
          </button>
        </div>
      </EditModal>
    </div>
  );
};

export default CustomerDetailsWidget;
